import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, X, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const MAX_DURATION = 120; // 2 minutes in seconds

export default function VoiceRecorder({ onSend, onCancel, disabled }) {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const startedRef = useRef(false);

  // Auto-start recording when component mounts
  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true;
      startRecording();
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { 
          type: mediaRecorder.mimeType 
        });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start(100);
      setIsRecording(true);
      setDuration(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(prev => {
          if (prev >= MAX_DURATION - 1) {
            stopRecording();
            return MAX_DURATION;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setPermissionError(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration(0);
    onCancel?.();
  };

  const handleSend = async () => {
    if (!audioBlob) return;
    
    setSending(true);
    try {
      await onSend(audioBlob, duration);
      setAudioBlob(null);
      setAudioUrl(null);
      setDuration(0);
    } catch (error) {
      console.error('Error sending voice message:', error);
    } finally {
      setSending(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Permission denied
  if (permissionError) {
    return (
      <div className="flex flex-col items-center gap-2 bg-red-50 px-4 py-3 rounded-xl max-w-sm" data-testid="mic-permission-denied">
        <div className="flex items-center gap-2 w-full">
          <Mic className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-red-700 text-sm font-medium">Microphone access denied</span>
          <button
            type="button"
            onClick={cancelRecording}
            className="ml-auto p-1 hover:bg-red-100 rounded-full text-red-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-xs text-red-600/80 leading-relaxed w-full">
          <p className="mb-1.5">To enable your microphone:</p>
          <ol className="list-decimal ml-4 space-y-0.5">
            <li>Click the <strong>lock icon</strong> (or site info icon) in your browser's address bar</li>
            <li>Find <strong>Microphone</strong> and set it to <strong>Allow</strong></li>
            <li>Refresh the page and try again</li>
          </ol>
          <p className="mt-1.5 text-red-500/70">If viewing in a preview frame, open the site in its own tab for microphone access.</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setPermissionError(false);
            startedRef.current = false;
            startRecording();
          }}
          className="text-xs text-red-600 hover:text-red-800 underline mt-1 transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  // Waiting for permission / starting
  if (!isRecording && !audioBlob) {
    return (
      <div className="flex items-center gap-2 bg-amber-50 px-3 py-2 rounded-full">
        <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
        <span className="text-amber-600 text-sm">Starting microphone...</span>
        <button
          type="button"
          onClick={cancelRecording}
          className="p-1 hover:bg-amber-100 rounded-full text-amber-500 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Recording in progress
  if (isRecording) {
    return (
      <div className="flex items-center gap-2 bg-red-50 px-3 py-2 rounded-full">
        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
        <span className="text-red-600 font-medium text-sm">
          {formatDuration(duration)} / {formatDuration(MAX_DURATION)}
        </span>
        <button
          type="button"
          onClick={stopRecording}
          className="p-1 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
          title="Stop recording"
        >
          <Square className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={cancelRecording}
          className="p-1 hover:bg-red-100 rounded-full text-red-500 transition-colors"
          title="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Recording complete - show preview
  return (
    <div className="flex items-center gap-2 bg-purple-50 px-3 py-2 rounded-full">
      <audio src={audioUrl} controls className="h-8 max-w-[150px]" />
      <span className="text-purple-600 text-sm font-medium">
        {formatDuration(duration)}
      </span>
      <Button
        type="button"
        onClick={handleSend}
        disabled={sending}
        size="sm"
        className="btn-primary rounded-full px-3 py-1 h-8"
      >
        {sending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Send className="w-4 h-4" />
        )}
      </Button>
      <button
        type="button"
        onClick={cancelRecording}
        className="p-1 hover:bg-purple-100 rounded-full text-purple-500 transition-colors"
        title="Cancel"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
